<template>
  <div class="ticket-description">
    <label class="section-label">{{ title }}</label>

    <!-- Display mode -->
    <div v-if="!editing"
         class="description-display"
         :class="{ 'description-display--editable': editable }"
         @click="startEdit">
      <div v-if="hasContent" class="description-content" v-html="renderedHtml"></div>
      <span v-else class="description-placeholder">{{ editable ? placeholder : emptyText }}</span>
    </div>

    <!-- Edit mode -->
    <div v-else>
      <rich-text-editor
        ref="editor"
        v-model="draftHtml"
        toolbar="comment"
        :placeholder="placeholder"
        height="auto"
        :dark="dark"
      />
      <div class="description-actions">
        <base-button size="sm" type="default" @click="cancelEdit" :disabled="saving">Cancel</base-button>
        <base-button size="sm" type="primary" class="ml-2" @click="save" :disabled="saving">
          <i v-if="saving" class="el-icon-loading mr-1"></i>{{ saving ? 'Saving...' : 'Save changes' }}
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
// Read-only or click-to-edit ticket description. Renders rich text editor HTML
// on the card surface in either theme. The editor's default text color (black
// in light mode, white in dark mode) is stripped so the surface color applies;
// colors chosen from the editor palette are kept.
//
// Saving is the parent's job: listen for `save` (html), persist, then set
// `value` to the new HTML and flip `saving` back to false. Call `close()` on
// success to leave edit mode, or leave it open on failure.
import RichTextEditor from '@/components/RichTextEditor'
import { sanitizeQuillHtml } from '@/util/quillSanitize'
import { stripDefaultTextColors, isEmptyHtml } from '@/util/richText'

export default {
  name: 'ticket-description',
  components: { RichTextEditor },
  props: {
    value: { type: String, default: '' },
    title: { type: String, default: 'Description' },
    editable: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    dark: { type: Boolean, default: true },
    placeholder: { type: String, default: 'Click to add a description...' },
    emptyText: { type: String, default: 'No description.' }
  },
  data() {
    return {
      editing: false,
      draftHtml: ''
    }
  },
  computed: {
    hasContent() {
      return !isEmptyHtml(this.value)
    },
    renderedHtml() {
      return stripDefaultTextColors(sanitizeQuillHtml(this.value || ''))
    }
  },
  methods: {
    startEdit() {
      if (!this.editable || this.editing) return
      this.editing = true
      this.draftHtml = this.value || ''
      this.$nextTick(() => {
        if (this.$refs.editor) this.$refs.editor.setContent(this.value || '')
      })
    },
    cancelEdit() {
      this.editing = false
      this.$emit('cancel')
    },
    save() {
      const html = this.$refs.editor ? this.$refs.editor.getContent() : this.draftHtml
      this.$emit('save', html)
    },
    // Parent calls this after a successful save.
    close() {
      this.editing = false
    }
  }
}
</script>

<style scoped>
.ticket-description {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.section-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 8px;
}
.description-display {
  position: relative;
  border-radius: 6px;
  min-height: 44px;
  transition: box-shadow 0.15s ease;
}
.description-display--editable {
  cursor: pointer;
}
.description-display--editable:hover {
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.15);
}
.description-content {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  max-height: 400px;
  overflow-y: auto;
}
/* Theme rules such as .card p paint text white or dark. Inherit from the
   surface instead. No !important, so inline palette colors from the editor
   still win. */
.description-content ::v-deep * {
  color: inherit;
}
.description-content ::v-deep p {
  margin-bottom: 6px;
}
.description-content ::v-deep p:last-child {
  margin-bottom: 0;
}
.description-content ::v-deep ol {
  list-style-type: decimal;
  padding-left: 2em;
}
.description-content ::v-deep ul {
  list-style-type: disc;
  padding-left: 2em;
}
.description-content ::v-deep a {
  color: #1d8cf8;
}
.description-content ::v-deep .checklist-item {
  list-style: none !important;
  margin-left: -1.5em;
}
.description-content ::v-deep .checklist-cb {
  appearance: none;
  -webkit-appearance: none;
  width: 14px;
  height: 14px;
  border: 1px solid rgba(125, 125, 125, 0.4);
  border-radius: 3px;
  background: transparent;
  vertical-align: middle;
  position: relative;
  margin-right: 6px;
}
.description-content ::v-deep .checklist-cb:checked {
  background-color: #e14eca;
  border-color: #e14eca;
}
.description-content ::v-deep .checklist-cb:checked::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 0px;
  width: 5px;
  height: 9px;
  border: solid white;
  border-width: 0 1.5px 1.5px 0;
  transform: rotate(45deg);
}
.description-placeholder {
  display: block;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.3);
  font-style: italic;
  padding: 10px 12px;
}
.description-actions {
  display: flex;
  align-items: center;
  padding: 8px 0 0 0;
}

/* Light mode */
.white-content .ticket-description {
  border-bottom-color: rgba(0, 0, 0, 0.08);
}
.white-content .section-label {
  color: #525f7f;
}
.white-content .description-display--editable:hover {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
}
.white-content .description-content {
  color: #344675;
}
.white-content .description-placeholder {
  color: #9A9A9A;
}
</style>
